import { Link, useParams } from "react-router-dom";

import { ArrowLeft, FileText } from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { EmptyState } from "~/components/ui/empty-state";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { TablePagination } from "~/components/ui/table-pagination";
import { useSupplier, useSupplierOpenBalances } from "~/hooks/purchasing";
import { usePagination } from "~/hooks/usePagination";
import { formatCurrency, formatDate } from "~/utils/helpers";

import { SupplierCompanyProfilesCard } from "./components/SupplierCompanyProfilesCard";

import type { OpeningBalanceEstado } from "~/types/purchasing";

const ESTADO_BADGE: Record<
  OpeningBalanceEstado,
  { label: string; className: string }
> = {
  vencida: { label: "Vencida", className: "bg-red-100 text-red-800" },
  por_vencer: { label: "Por vencer", className: "bg-green-100 text-green-800" },
  sin_fecha: { label: "Sin fecha", className: "bg-gray-100 text-gray-700" },
};

const clp = (n: number) => `$${formatCurrency(n)}`;

function InfoField({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="text-sm text-gray-800">{value || "-"}</dd>
    </div>
  );
}

export function SupplierDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: supplier, isLoading: loadingSupplier } = useSupplier(id);
  const { data: docs = [], isLoading: loadingDocs } = useSupplierOpenBalances(id);

  const pagination = usePagination(docs, 15);

  const backButton = (
    <Button asChild variant="ghost" size="sm" className="-ml-2">
      <Link to="/contabilidad/proveedores">
        <ArrowLeft className="mr-1 h-4 w-4" />
        Volver
      </Link>
    </Button>
  );

  if (loadingSupplier) {
    return (
      <PageContainer>
        <Skeleton className="mb-6 h-9 w-64" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      </PageContainer>
    );
  }

  if (!supplier) {
    return (
      <PageContainer>
        <PageHeader title="Proveedor no encontrado" back={backButton} />
      </PageContainer>
    );
  }

  const total = docs.reduce((s, d) => s + d.saldo_clp, 0);
  const overdue = docs
    .filter((d) => d.estado === "vencida")
    .reduce((s, d) => s + d.saldo_clp, 0);

  return (
    <PageContainer>
      <PageHeader
        title={supplier.name}
        description={
          supplier.softland_code
            ? `Código Softland ${supplier.softland_code}`
            : undefined
        }
        back={backButton}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              variant="secondary"
              className="bg-gray-100 text-gray-800"
            >
              {supplier.is_foreign ? "Extranjero" : "Nacional"}
            </Badge>
            <Badge
              variant={supplier.is_active ? "default" : "secondary"}
              className={
                supplier.is_active
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-800"
              }
            >
              {supplier.is_active ? "Activo" : "Inactivo"}
            </Badge>
          </div>
        }
      />

      {/* KPI summary */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Saldo abierto
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{clp(total)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Vencido
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-red-700">{clp(overdue)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Documentos abiertos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{docs.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Datos del proveedor */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Datos del proveedor</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
            <InfoField label="Razón social" value={supplier.razon_social} />
            <InfoField label="RUT" value={supplier.rut} />
            <InfoField label="Código Softland" value={supplier.softland_code} />
            <InfoField label="Condición de pago" value={supplier.payment_condition} />
            <InfoField label="Giro" value={supplier.giro} />
            <InfoField label="Banco" value={supplier.bank_name} />
            <InfoField label="Cuenta bancaria" value={supplier.bank_account} />
            <InfoField label="Contacto" value={supplier.contact_name} />
            <InfoField label="Email" value={supplier.email} />
            <InfoField label="Teléfono" value={supplier.phone} />
            <InfoField label="Dirección" value={supplier.address} />
            <InfoField label="Comuna" value={supplier.comuna} />
          </dl>
        </CardContent>
      </Card>

      {/* Cuentas por empresa (multiempresa AP) */}
      <SupplierCompanyProfilesCard supplierId={supplier.id} />

      {/* Estado de cuenta */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">
            Estado de cuenta
            <span className="ml-2 font-normal text-muted-foreground">
              (saldo inicial 31/07 + facturas)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loadingDocs ? (
            <div className="space-y-2 p-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : docs.length === 0 ? (
            <EmptyState
              icon={FileText}
              title="Sin documentos abiertos"
              description="Este proveedor no tiene saldos pendientes al corte."
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Folio</TableHead>
                    <TableHead>Documento</TableHead>
                    <TableHead>Origen</TableHead>
                    <TableHead>Emisión</TableHead>
                    <TableHead>Vencimiento</TableHead>
                    <TableHead className="text-right">Saldo</TableHead>
                    <TableHead>Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pagination.pageItems.map((d) => {
                    const badge = ESTADO_BADGE[d.estado];
                    const overdueDays =
                      d.days_to_due != null && d.days_to_due < 0
                        ? -d.days_to_due
                        : null;
                    return (
                      <TableRow key={d.id}>
                        <TableCell className="font-medium">
                          {d.folio || "-"}
                          {d.doc_tipo_code && (
                            <span className="ml-1 text-xs text-muted-foreground">
                              {d.doc_tipo_code}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="max-w-[22rem]">
                          <div className="truncate text-gray-700">
                            {d.document_type || "-"}
                          </div>
                          {d.original_currency === "USD" &&
                            d.original_amount != null && (
                              <div className="text-xs text-muted-foreground">
                                US$ {formatCurrency(d.original_amount, "USD")}
                                {d.exchange_rate != null &&
                                  ` · TC ${formatCurrency(d.exchange_rate, "USD")}`}
                              </div>
                            )}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="secondary"
                            className={
                              d.origin === "Factura"
                                ? "bg-blue-50 text-blue-700"
                                : "bg-gray-100 text-gray-600"
                            }
                          >
                            {d.origin}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {d.emission_date ? formatDate(d.emission_date) : "-"}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {d.due_date ? formatDate(d.due_date) : "-"}
                        </TableCell>
                        <TableCell
                          className={`text-right font-medium ${
                            d.saldo_clp > 0 ? "text-green-700" : ""
                          }`}
                        >
                          {clp(d.saldo_clp)}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="secondary"
                            className={badge.className}
                          >
                            {badge.label}
                            {overdueDays != null && ` · ${overdueDays} d`}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <div className="border-t p-4">
                <TablePagination
                  page={pagination.page}
                  pageCount={pagination.pageCount}
                  totalItems={pagination.total}
                  pageSize={15}
                  onPageChange={pagination.setPage}
                  itemLabel="documentos"
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  );
}
